<script setup lang="ts">
import { marked, type Renderer, type Tokens } from 'marked';
import DOMPurify from 'dompurify';
import { escapeHtml } from '~/utils/escapeHtml';
import { UNTRUSTED_MEDIA_SANITIZE, answerPurifier, isTrustedImageUrl, withToolImageUrls } from '~/utils/trustedMedia';
import { ASK_PREVIEW_LIMIT } from '~/utils/askPreviewLimit';
import { localizeAskLabel } from '~/utils/askStructureLabels';
import { parseConfidenceValue, type ConfidenceLevel } from '~/utils/askConfidenceLevel';

const { t, locale } = useI18n();

// Renders model-generated markdown safely. We use `marked` for parsing
// (fast, GFM-aware so pipe-tables work) and `DOMPurify` to strip anything
// that isn't a vetted tag — the model could theoretically emit raw HTML
// inside the markdown and we don't want it injecting <script> or onclick.
//
// Streaming-friendly: re-renders on every input change (each token append
// re-parses the partial markdown). `marked.parse` is fast enough at this
// length that the cost is invisible.

const {
  text = '',
  threadTitle = '',
  isStreaming = false,
  toolUrls = undefined,
} = defineProps<{
  text?: string;
  threadTitle?: string;
  isStreaming?: boolean;
  /** The answer's own tool-result URLs (toolResultUrls): an image at exactly one of them loads. */
  toolUrls?: ReadonlySet<string>;
}>();

const emit = defineEmits<{
  (e: 'send-prompt', text: string): void;
  (e: 'reply', payload: { label: string; detail: string }): void;
}>();

marked.setOptions({
  gfm: true,
  breaks: true,
});

// Render all links with target="_blank" rel="noopener noreferrer". External
// (https) URLs always open in a new tab; agent expects the chat to stay
// "alive" while they reference the project page. Relative URLs (same-site)
// also open in a new tab to preserve the conversation state.
// marked calls each override on its own renderer, the one holding the parser (`this`): read through this object,
// the parser was unset and every link's text rendered empty, rebuilt from its URL by enhanceLinks below.
const linkRenderer = new marked.Renderer();
linkRenderer.link = function (this: Renderer, { href, title, tokens }: Tokens.Link) {
  const inner = this.parser.parseInline(tokens);
  const titleAttr = title ? ` title="${title.replace(/"/g, '&quot;')}"` : '';
  return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${inner}</a>`;
};
// Explicitly render images as <img> tags. Without this override, newer versions
// of marked delegate the image token to the link renderer, producing an <a> tag
// instead of an <img> — causing Google Cloud Storage image URLs to render as
// filename hyperlinks rather than photos.
// Only our own media hosts load as images (utils/trustedMedia.ts): any other URL becomes a link, which needs a click,
// so an image URL the model wrote cannot carry data out on its own.
linkRenderer.image = ({ href, title, text }) => {
  if (!href) return text || '';
  if (!isTrustedImageUrl(href)) {
    const label = escapeHtml(text || href);
    return `<a href="${href.replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer nofollow">${label}</a>`;
  }
  const safeHref = href.replace(/"/g, '&quot;');
  const altAttr = ` alt="${(text || '').replace(/"/g, '&quot;')}"`;
  const titleAttr = title ? ` title="${title.replace(/"/g, '&quot;')}"` : '';
  return `<img src="${safeHref}"${altAttr}${titleAttr}>`;
};
// A fence the model labels markdown holds the answer itself ("give me a checklist as a markdown task list" came
// back as raw `- [ ]` text in a code box): it is rendered as the rest of the answer is, and sanitised with it.
const MARKDOWN_FENCE_LANGS = new Set(['markdown', 'md', 'gfm']);
const renderCode = linkRenderer.code.bind(linkRenderer);
linkRenderer.code = function (this: Renderer, token: Tokens.Code) {
  const lang = (token.lang ?? '').trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  if (!MARKDOWN_FENCE_LANGS.has(lang)) return renderCode(token);
  return this.parser.parse(marked.lexer(token.text));
};
marked.use({ renderer: linkRenderer });

const NO_TOOL_URLS: ReadonlySet<string> = new Set();

const html = computed(() => {
  if (!text) return '';
  if (typeof window === 'undefined') {
    // SSR: DOMPurify needs a window, so the server sends the text escaped, never unsanitised HTML a browser would
    // render (an <img> in it would load before the client sanitised it). The client renders the markup on mount.
    return escapeHtml(text);
  }
  return withToolImageUrls(toolUrls ?? NO_TOOL_URLS, () =>
    answerPurifier(() => DOMPurify(window)).sanitize(marked.parse(text, { async: false }) as string, {
      ADD_ATTR: ['target', 'rel'],
      FORBID_TAGS: [...UNTRUSTED_MEDIA_SANITIZE.FORBID_TAGS],
      FORBID_ATTR: [...UNTRUSTED_MEDIA_SANITIZE.FORBID_ATTR],
    }),
  );
});

// ---------------------------------------------------------------------------
// CSV export: after the rendered HTML mounts, scan for <table> nodes and
// inject a small "Export CSV" button below each one. The button reads the
// table's headers + rows from the DOM at click time (so streaming-late
// updates still work), serialises to CSV with standard escaping, and
// triggers a browser download.
// ---------------------------------------------------------------------------
const rootRef = ref<HTMLElement | null>(null);

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const csvEscape = (cell: string): string => {
  // Standard RFC 4180-ish: wrap in quotes if contains comma, quote, or newline.
  // Double any embedded quotes.
  const needs = /[",\n\r]/.test(cell);
  const escaped = cell.replace(/"/g, '""');
  return needs ? `"${escaped}"` : escaped;
};

const tableToCsv = (table: HTMLTableElement): string => {
  const lines: string[] = [];
  // Walk every row (thead + tbody + tfoot). textContent strips inline tags so
  // markdown thumbnails / links collapse cleanly into their text.
  const rows = table.querySelectorAll('tr');
  for (const row of Array.from(rows)) {
    const cells = Array.from(row.querySelectorAll('th, td')).filter(
      (c) => !(c as HTMLElement).classList.contains('ask-row-reply-cell'),
    );
    if (cells.length === 0) continue;
    const values = Array.from(cells).map((c) => {
      const txt = (c.textContent ?? '').replace(/\s+/g, ' ').trim();
      return csvEscape(txt);
    });
    lines.push(values.join(','));
  }
  return lines.join('\n');
};

const triggerDownload = (csv: string, filename: string) => {
  // Prepend BOM so Excel detects UTF-8 correctly.
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const buildFilename = (): string => {
  const ts = new Date()
    .toISOString()
    .replace(/[:.]/g, '-')
    .replace(/-\d{3}Z$/, 'Z');
  const slug = threadTitle ? slugify(threadTitle) : '';
  const base = slug ? `insiders-ask-${slug}` : 'insiders-ask-results';
  return `${base}-${ts}.csv`;
};

const applyTableLimit = (wrap: HTMLElement, table: HTMLTableElement) => {
  const rows = Array.from(table.tBodies).flatMap((body) => Array.from(body.rows));
  const existing = wrap.querySelector<HTMLButtonElement>('.ask-table-more-btn');
  if (rows.length <= ASK_PREVIEW_LIMIT) {
    rows.forEach((row) => row.classList.remove('ask-table-row-hidden'));
    existing?.remove();
    return;
  }

  const expanded = wrap.dataset.askExpanded === '1';
  rows.forEach((row, index) => {
    row.classList.toggle('ask-table-row-hidden', !expanded && index >= ASK_PREVIEW_LIMIT);
  });

  let btn = existing;
  if (!btn) {
    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ask-table-more-btn';
    btn.addEventListener('click', () => {
      wrap.dataset.askExpanded = wrap.dataset.askExpanded === '1' ? '0' : '1';
      applyTableLimit(wrap, table);
    });
    wrap.appendChild(btn);
  }
  setToggleButtonLabel(btn, expanded, rows.length);
};

// Renders the toggle's text via DOM APIs (not innerHTML) so the arrow glyph
// stays in its own element for RTL mirroring, matching the template-side
// P6b pattern used elsewhere in this batch.
const setToggleButtonLabel = (btn: HTMLButtonElement, expanded: boolean, count: number) => {
  btn.textContent = '';
  if (expanded) {
    btn.append(document.createTextNode(t('ask.markdownContent.hideRows')));
    return;
  }
  btn.append(document.createTextNode(`${t('ask.markdownContent.viewAllRows', { count }, count)} `));
  const arrow = document.createElement('span');
  arrow.setAttribute('aria-hidden', 'true');
  arrow.className = 'rtl:inline-block rtl:-scale-x-100';
  arrow.textContent = '→';
  btn.appendChild(arrow);
};

const setScrollHintLabel = (hint: HTMLElement) => {
  hint.textContent = '';
  hint.append(document.createTextNode(`${t('ask.markdownContent.scrollTableHint')} `));
  const arrow = document.createElement('span');
  arrow.setAttribute('aria-hidden', 'true');
  arrow.className = 'rtl:inline-block rtl:-scale-x-100';
  arrow.textContent = '→';
  hint.appendChild(arrow);
};

const REPLY_ICON_SVG =
  `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ` +
  `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
  `<polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>`;

const cellText = (cell: Element): string => (cell.textContent ?? '').replace(/\s+/g, ' ').trim();

// Column headers, so a quoted row reads as "Date=21 Jul 2026 | Price=AED 4.45M"
// rather than a bare list of values the model would have to re-guess.
const headerRowFor = (table: HTMLTableElement): HTMLTableRowElement | null =>
  table.tHead?.rows[0] ?? table.querySelector('tr');

const headerLabelsFor = (headerRow: HTMLTableRowElement): string[] =>
  Array.from(headerRow.cells)
    .filter((c) => !c.classList.contains('ask-row-reply-cell'))
    .map((c) => cellText(c));

// Adds a per-row reply control to a rendered markdown table. The model's prose
// tables are what an agent actually sees in the answer, so this is where the
// "reply to this specific thing" affordance has to live — the tool-result cards
// are collapsed behind a toggle and rarely opened.
const addRowReplyControls = (table: HTMLTableElement) => {
  const headerRow = headerRowFor(table);
  if (!headerRow) return;
  const headers = headerLabelsFor(headerRow);
  if (headers.length === 0) return;

  // The spacer must land on whichever row supplied the labels, otherwise a
  // table without a <thead> gains a body cell with no matching header and every
  // column shifts by one.
  if (!headerRow.querySelector('.ask-row-reply-cell')) {
    const spacer = document.createElement(headerRow.parentElement === table.tHead ? 'th' : 'td');
    spacer.className = 'ask-row-reply-cell';
    spacer.setAttribute('aria-label', t('ask.common.reply'));
    headerRow.appendChild(spacer);
  }

  const bodyRows = Array.from(table.tBodies)
    .flatMap((body) => Array.from(body.rows))
    .filter((row) => row !== headerRow);
  for (const row of bodyRows) {
    if (row.dataset.askReplyBound === '1') continue;
    row.dataset.askReplyBound = '1';

    const values = Array.from(row.cells).map((c) => cellText(c));
    const td = document.createElement('td');
    td.className = 'ask-row-reply-cell';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ask-row-reply-btn';
    btn.title = t('ask.markdownContent.replyToRowLabel');
    btn.setAttribute('aria-label', t('ask.markdownContent.replyToRowLabel'));
    btn.innerHTML = REPLY_ICON_SVG;
    btn.addEventListener('click', () => {
      const fields = values
        .map((value, index) => (value ? `${headers[index] ?? `col${index + 1}`}=${value}` : ''))
        .filter(Boolean)
        .join(' | ');
      if (!fields) return;
      emit('reply', {
        label: values.filter(Boolean).slice(0, 3).join(' · '),
        detail: `[REFERRING TO TABLE ROW: ${fields}]`,
      });
    });

    td.appendChild(btn);
    row.appendChild(td);
  }
};

const enhanceTables = () => {
  const root = rootRef.value;
  if (!root) return;
  const tables = root.querySelectorAll('table');
  for (const table of Array.from(tables)) {
    if (table.parentElement?.classList.contains('ask-table-wrap')) {
      applyTableLimit(table.parentElement as HTMLElement, table as HTMLTableElement);
      if (!table.parentElement.querySelector('.ask-table-scroll-hint')) {
        const hint = document.createElement('div');
        hint.className = 'ask-table-scroll-hint';
        setScrollHintLabel(hint);
        table.parentElement.insertBefore(hint, table);
      }
      addRowReplyControls(table as HTMLTableElement);
      continue;
    }

    const wrap = document.createElement('div');
    wrap.className = 'ask-table-wrap';
    table.parentNode?.insertBefore(wrap, table);
    const hint = document.createElement('div');
    hint.className = 'ask-table-scroll-hint';
    setScrollHintLabel(hint);
    wrap.appendChild(hint);
    wrap.appendChild(table);
    applyTableLimit(wrap, table as HTMLTableElement);
    addRowReplyControls(table as HTMLTableElement);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ask-csv-btn';
    btn.textContent = t('ask.markdownContent.exportCsv');
    btn.addEventListener('click', () => {
      const csv = tableToCsv(table as HTMLTableElement);
      triggerDownload(csv, buildFilename());
    });
    wrap.appendChild(btn);
  }
};

// Broken-image fallback. The model occasionally cites image URLs that 404
// or that the agent's tier can't access; without a fallback the table shows
// the browser's broken-image glyph. We swap to a neutral placeholder div
// (gray box with a subtle photo glyph) via JS so the cell stays clean.
const PLACEHOLDER_SVG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 32"><rect width="44" height="32" fill="%23e8e8e3"/><path d="M12 12l5 6 4-4 7 8H8z" fill="%23c0bfb8"/><circle cx="32" cy="11" r="2.5" fill="%23c0bfb8"/></svg>`,
  );

const enhanceImages = () => {
  const root = rootRef.value;
  if (!root) return;
  const imgs = root.querySelectorAll<HTMLImageElement>('img');
  for (const img of Array.from(imgs)) {
    if (img.dataset.askFallbackBound === '1') continue;
    img.dataset.askFallbackBound = '1';

    const src = img.getAttribute('src') ?? '';
    // Empty src means the AI signaled "no image available" (![Project image pending]())
    if (!src) {
      const ph = document.createElement('div');
      ph.className = 'ask-img-pending';
      ph.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9l4-4 4 4 4-4 4 4"/><circle cx="8.5" cy="13.5" r="1.5"/></svg>`;
      const label = document.createElement('span');
      label.textContent = t('ask.markdownContent.projectImagePending');
      ph.appendChild(label);
      img.parentNode?.replaceChild(ph, img);
      continue;
    }

    img.addEventListener('error', () => {
      if (img.src !== PLACEHOLDER_SVG) img.src = PLACEHOLDER_SVG;
    });
  }
};

// Price Position badges. The AI outputs **Price Position: PREMIUM** (or FAIR /
// OVERPRICED / BELOW MARKET / INSUFFICIENT DATA) when answering pricing questions.
// We detect those <strong> nodes and swap them for styled badge chips.
const PRICE_BADGE_VERDICTS = ['BELOW MARKET', 'FAIR', 'PREMIUM', 'OVERPRICED', 'INSUFFICIENT DATA'] as const;
// A listing check's verdicts (server/utils/ai/listingBenchmark.ts OVERALL_VERDICT_LABELS), then the
// older wording some saved answers still carry, so those keep their badge too.
const OVERALL_VERDICT_LABELS = [
  'STRONG OPPORTUNITY',
  'ATTRACTIVE PRICE',
  'FAIR DEAL',
  'PREMIUM PRICED',
  'NEEDS CAUTION',
  'STRONG BUY',
  'GOOD BUY',
  'NEGOTIATE',
  'OVERPRICED',
  'AVOID',
] as const;

// The AI is prompted to always emit these markers in English (server-side
// system prompt, out of Step 3 scope) — the detection regexes above must
// keep matching that English text. Only the rendered badge below is
// translated, via these key maps.
const PRICE_BADGE_LABEL_KEYS: Record<(typeof PRICE_BADGE_VERDICTS)[number], string> = {
  'BELOW MARKET': 'ask.markdownContent.priceBadgeBelowMarket',
  FAIR: 'ask.markdownContent.priceBadgeFair',
  PREMIUM: 'ask.markdownContent.priceBadgePremium',
  OVERPRICED: 'ask.markdownContent.priceBadgeOverpriced',
  'INSUFFICIENT DATA': 'ask.markdownContent.priceBadgeInsufficientData',
};
const OVERALL_VERDICT_LABEL_KEYS: Record<(typeof OVERALL_VERDICT_LABELS)[number], string> = {
  'STRONG OPPORTUNITY': 'ask.markdownContent.verdictStrongOpportunity',
  'ATTRACTIVE PRICE': 'ask.markdownContent.verdictAttractivePrice',
  'PREMIUM PRICED': 'ask.markdownContent.verdictPremiumPriced',
  'NEEDS CAUTION': 'ask.markdownContent.verdictNeedsCaution',
  'STRONG BUY': 'ask.markdownContent.verdictStrongBuy',
  'GOOD BUY': 'ask.markdownContent.verdictGoodBuy',
  'FAIR DEAL': 'ask.markdownContent.verdictFairDeal',
  NEGOTIATE: 'ask.markdownContent.verdictNegotiate',
  OVERPRICED: 'ask.markdownContent.priceBadgeOverpriced',
  AVOID: 'ask.markdownContent.verdictAvoid',
};

// Single DOM scan handles both Price Position and Overall Verdict badges to
// avoid iterating the same strong/b NodeList twice on every streaming update.
const enhanceBadges = () => {
  const root = rootRef.value;
  if (!root) return;
  const strongs = root.querySelectorAll<HTMLElement>('strong, b');
  for (const el of Array.from(strongs)) {
    if (el.dataset.askPriceBound === '1' || el.dataset.askVerdictBound === '1') continue;
    const text = (el.textContent ?? '').trim();

    const priceMatch = text.match(/^Price Position:\s*(.+)$/i);
    if (priceMatch) {
      const rawLabel = priceMatch[1]?.trim().toUpperCase() ?? '';
      const verdict = PRICE_BADGE_VERDICTS.find((v) => rawLabel === v);
      if (verdict) {
        el.dataset.askPriceBound = '1';
        const badge = document.createElement('span');
        badge.className = 'ask-price-badge';
        badge.dataset.verdict = verdict;
        badge.textContent = t(PRICE_BADGE_LABEL_KEYS[verdict]);
        el.parentNode?.replaceChild(badge, el);
        continue;
      }
    }

    const verdictMatch = text.match(/^Overall Verdict:\s*(.+)$/i);
    if (verdictMatch) {
      const rawLabel = verdictMatch[1]?.trim().toUpperCase() ?? '';
      const label = OVERALL_VERDICT_LABELS.find((v) => rawLabel === v);
      if (label) {
        el.dataset.askVerdictBound = '1';
        const badge = document.createElement('span');
        badge.className = 'ask-verdict-badge';
        badge.dataset.verdict = label;
        badge.textContent = t(OVERALL_VERDICT_LABEL_KEYS[label]);
        el.parentNode?.replaceChild(badge, el);
      }
    }
  }
};

const availabilityToneFor = (label: string): 'good' | 'warn' | 'bad' | 'neutral' => {
  if (/sold|unavailable|low confidence|stale/i.test(label)) return 'bad';
  if (/need|confirm|limited|medium|pending/i.test(label)) return 'warn';
  if (/live|verified|confirmed|recent|checked|high/i.test(label)) return 'good';
  return 'neutral';
};

// The AI is prompted to always emit these status phrases in English — the
// detection regex below must keep matching that English text. Only the
// rendered badge is translated, via this key map (the English value for
// each key is the same title-cased text the old formatBadgeLabel() produced,
// so the display is byte-identical when untranslated).
const AVAILABILITY_LABEL_KEYS: Record<string, string> = {
  'live confirmed': 'ask.markdownContent.availabilityLiveConfirmed',
  'recently checked': 'ask.markdownContent.availabilityRecentlyChecked',
  verified: 'ask.markdownContent.availabilityVerified',
  'needs confirmation': 'ask.markdownContent.availabilityUnconfirmed',
  'availability unconfirmed': 'ask.markdownContent.availabilityUnconfirmed',
  'high confidence': 'ask.markdownContent.availabilityHighConfidence',
  'medium confidence': 'ask.markdownContent.availabilityMediumConfidence',
  'low confidence': 'ask.markdownContent.availabilityLowConfidence',
  'limited stock': 'ask.markdownContent.availabilityLimitedStock',
  'sold out': 'ask.markdownContent.availabilitySoldOut',
  unavailable: 'ask.markdownContent.availabilityUnavailable',
};

// Title-cases an English marker ("sold out" → "Sold Out") — the pre-i18n badge text.
const toTitleCase = (value: string): string => value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

type AvailabilityLabel = {
  /** English label exactly as rendered before i18n — drives tone + the `remaining` slice. */
  source: string;
  /** Text shown in the badge (translated for known statuses). */
  display: string;
};

const availabilityLabelFrom = (value: string): AvailabilityLabel => {
  const known = value.match(
    /\b(Live confirmed|Recently checked|Verified|Needs confirmation|Availability unconfirmed|High confidence|Medium confidence|Low confidence|Limited stock|Sold out|Unavailable)\b/i,
  )?.[1];
  if (known) {
    const source = /needs confirmation/i.test(known) ? 'Availability Unconfirmed' : toTitleCase(known);
    const key = AVAILABILITY_LABEL_KEYS[known.toLowerCase()];
    return { source, display: key ? t(key) : source };
  }
  const firstPhrase = value.split(/[.;,|]/)[0]?.trim() ?? value.trim();
  const source = firstPhrase.length > 36 ? `${firstPhrase.slice(0, 33).trim()}...` : firstPhrase;
  return { source, display: source };
};

const confidenceBadgeLabel = (level: ConfidenceLevel): AvailabilityLabel => {
  const source = toTitleCase(`${level} confidence`);
  const key = AVAILABILITY_LABEL_KEYS[`${level} confidence`];
  return { source, display: key ? t(key) : source };
};

const enhanceAvailabilityBadges = () => {
  const root = rootRef.value;
  if (!root) return;
  const blocks = root.querySelectorAll<HTMLElement>('p, li');
  for (const block of Array.from(blocks)) {
    if (block.dataset.askAvailabilityBound === '1') continue;
    const text = (block.textContent ?? '').replace(/\s+/g, ' ').trim();
    const match = text.match(/^(?:📍\s*)?(?:(?:Availability|Data)\s+confidence|Availability)\s*[:\-–—]\s*(.+)$/i);
    if (!match?.[1]) continue;

    const value = match[1].trim();
    // "Data Confidence: MEDIUM (reason)": the level is the badge, the reason the text beside it.
    const dataLevel = /^(?:📍\s*)?Data\s+confidence/i.test(text) ? parseConfidenceValue(value) : null;
    const label = dataLevel ? confidenceBadgeLabel(dataLevel.level) : availabilityLabelFrom(value);
    const remaining = dataLevel
      ? dataLevel.reason
      : value
          .slice(label.source.replace(/\.\.\.$/, '').length)
          .replace(/^[\s:;,.–—-]+/, '')
          .trim();

    block.dataset.askAvailabilityBound = '1';
    block.classList.add('ask-availability-confidence');
    block.textContent = '';

    const badge = document.createElement('span');
    badge.className = 'ask-availability-badge';
    badge.dataset.tone = availabilityToneFor(label.source);
    badge.textContent = label.display;
    block.appendChild(badge);

    if (remaining) {
      const copy = document.createElement('span');
      copy.className = 'ask-availability-copy';
      copy.textContent = remaining;
      block.appendChild(copy);
    }
  }
};

const htmlForNodes = (nodes: Node[]): string => {
  const wrap = document.createElement('div');
  for (const node of nodes) wrap.appendChild(node.cloneNode(true));
  return wrap.innerHTML.trim();
};

const textForHtml = (value: string): string => {
  const wrap = document.createElement('div');
  wrap.innerHTML = value;
  return (wrap.textContent ?? '').replace(/\s+/g, ' ').trim();
};

const paragraphSegments = (paragraph: HTMLParagraphElement): { html: string; text: string }[] => {
  const segments: { html: string; text: string }[] = [];
  let current: Node[] = [];
  for (const node of Array.from(paragraph.childNodes)) {
    if (node.nodeName === 'BR') {
      const html = htmlForNodes(current);
      const text = textForHtml(html);
      if (text) segments.push({ html, text });
      current = [];
      continue;
    }
    current.push(node);
  }
  const html = htmlForNodes(current);
  const text = textForHtml(html);
  if (text) segments.push({ html, text });
  return segments;
};

const keyValueFromSegment = (segment: { html: string; text: string }): { label: string; value: string } | null => {
  const separatorIndex = segment.text.indexOf(':');
  if (separatorIndex <= 1 || separatorIndex > 48) return null;
  const label = segment.text
    .slice(0, separatorIndex)
    .replace(/^[-•]\s*/, '')
    .trim();
  const valueText = segment.text.slice(separatorIndex + 1).trim();
  if (!label || !valueText) return null;
  if (label.split(' ').length > 6) return null;
  if (label.endsWith('extracted')) return null;

  const htmlSeparatorIndex = segment.html.indexOf(':');
  const rawValueHtml = htmlSeparatorIndex >= 0 ? segment.html.slice(htmlSeparatorIndex + 1).trim() : '';
  return { label, value: rawValueHtml || valueText };
};

const buildFactBlock = (facts: { label: string; value: string }[]): HTMLElement => {
  const block = document.createElement('section');
  block.className = 'ask-fact-block';
  const dl = document.createElement('dl');
  for (const fact of facts) {
    const row = document.createElement('div');
    row.className = 'ask-fact-row';
    const dt = document.createElement('dt');
    dt.textContent = fact.label;
    const dd = document.createElement('dd');
    dd.innerHTML = fact.value;
    row.append(dt, dd);
    dl.appendChild(row);
  }
  block.appendChild(dl);
  return block;
};

const elementFact = (element: HTMLElement): { label: string; value: string } | null => {
  const segments = paragraphSegments(element as HTMLParagraphElement);
  return segments.length === 1 ? keyValueFromSegment(segments[0]!) : null;
};

const enhanceFactBlocks = () => {
  const root = rootRef.value;
  if (!root) return;
  const paragraphs = root.querySelectorAll<HTMLParagraphElement>('p');
  for (const paragraph of Array.from(paragraphs)) {
    if (paragraph.dataset.askFactBound === '1') continue;
    if (paragraph.closest('.ask-fact-block')) continue;
    const segments = paragraphSegments(paragraph);
    if (segments.length < 3) continue;
    const facts = segments.map(keyValueFromSegment);
    if (facts.some((fact) => fact === null)) continue;

    const block = buildFactBlock(facts.filter((fact): fact is { label: string; value: string } => fact !== null));
    paragraph.dataset.askFactBound = '1';
    paragraph.parentNode?.replaceChild(block, paragraph);
  }
};

const enhanceConsecutiveFactLines = () => {
  const root = rootRef.value;
  if (!root) return;
  const children = Array.from(root.children) as HTMLElement[];
  let index = 0;

  while (index < children.length) {
    const group: HTMLElement[] = [];
    let cursor = index;
    while (cursor < children.length) {
      const child = children[cursor]!;
      if (child.dataset.askFactBound === '1' || child.closest('.ask-fact-block')) break;
      if (child.tagName !== 'P' && child.tagName !== 'LI') break;
      if (!elementFact(child)) break;
      group.push(child);
      cursor++;
    }

    if (group.length >= 3) {
      const facts = group
        .map((item) => elementFact(item))
        .filter((fact): fact is { label: string; value: string } => {
          return fact !== null;
        });
      const block = buildFactBlock(facts);
      group[0]?.parentNode?.insertBefore(block, group[0]);
      for (const item of group) {
        item.dataset.askFactBound = '1';
        item.remove();
      }
      index = cursor;
      continue;
    }

    index++;
  }
};

const enhanceInlineKeyValues = () => {
  const root = rootRef.value;
  if (!root) return;
  const blocks = root.querySelectorAll<HTMLElement>('p, li');
  for (const block of Array.from(blocks)) {
    if (block.dataset.askInlineKvBound === '1') continue;
    if (block.closest('.ask-fact-block')) continue;
    const fact = elementFact(block);
    if (!fact) continue;
    block.dataset.askInlineKvBound = '1';
    // label is plain text (from textContent) → escape; value is already-sanitized HTML.
    block.innerHTML = `<span class="ask-inline-kv-label">${escapeHtml(fact.label)}:</span> ${fact.value}`;
  }
};

// Backfill empty link text from the URL slug. Gemini occasionally emits
// `[](https://...)` — a markdown link with no display text — which renders
// as an invisible `<a>` in the table cell. We derive a humanised name from
// the URL's last path segment so the cell stays usable.
//   /projects/sobha-realty/310-riverside-crescent → "310 Riverside Crescent"
const humaniseSlug = (slug: string): string =>
  slug
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const enhanceLinks = () => {
  const root = rootRef.value;
  if (!root) return;
  const anchors = root.querySelectorAll('a');
  for (const a of Array.from(anchors)) {
    if ((a.textContent ?? '').trim().length > 0) continue;
    try {
      const href = a.getAttribute('href') ?? '';
      const path = href.split('?')[0].split('#')[0];
      const last = path.split('/').filter(Boolean).pop() ?? '';
      const name = last ? humaniseSlug(decodeURIComponent(last)) : t('ask.markdownContent.openLinkFallback');
      a.textContent = name;
    } catch {
      a.textContent = t('ask.markdownContent.openLinkFallback');
    }
  }
};

// Follow-up prompt chips. The model emits lines like "→ Compare Sobha vs
// Emaar" at the end of each answer. Turn each one into a clickable button
// that re-fires the prompt as the next user turn. Lines are split on <br>
// (marked.breaks=true emits these for line breaks) AND on paragraph
// boundaries so the chips appear regardless of how the model formatted them.
const enhanceFollowUps = () => {
  const root = rootRef.value;
  if (!root) return;
  const blocks = root.querySelectorAll('p, li');
  let renderedCount = 0;
  for (const block of Array.from(blocks)) {
    if ((block as HTMLElement).dataset.askFollowupBound === '1') continue;
    const inner = block.innerHTML;
    if (!inner.includes('→') && !inner.includes('&rarr;')) continue;
    // Split on <br> and rebuild. Lines starting with → become buttons;
    // everything else is preserved as-is.
    const lines = inner.split(/<br\s*\/?>|\n/);
    const hasFollow = lines.some((l) =>
      l
        .trim()
        .replace(/^&rarr;\s*/, '→ ')
        .startsWith('→'),
    );
    if (!hasFollow) continue;
    const rebuilt = lines
      .map((line) => {
        const trimmed = line.trim().replace(/^&rarr;\s*/, '→ ');
        if (!trimmed.startsWith('→')) return trimmed;
        if (renderedCount >= 4) return '';
        const promptHtml = trimmed.slice(1).trim();
        const tmp = document.createElement('div');
        tmp.innerHTML = promptHtml;
        const promptText = (tmp.textContent ?? '').trim();
        if (!promptText) return trimmed;
        const escaped = escapeHtml(promptText);
        // data-prompt keeps the English chip text (ask.vue matches it); only the label is localized.
        const label = displayLabel(promptText, 'chip');
        renderedCount++;
        return `<button type="button" class="ask-followup-chip" data-prompt="${escaped}">${label === promptText ? escaped : escapeHtml(label)}</button>`;
      })
      .filter((l) => l.length > 0)
      .join('');
    if (!rebuilt.includes('ask-followup-chip')) continue;
    block.innerHTML = rebuilt;
    (block as HTMLElement).dataset.askFollowupBound = '1';
    const btns = block.querySelectorAll('button.ask-followup-chip');
    btns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const p = (btn as HTMLButtonElement).dataset.prompt;
        if (p) emit('send-prompt', p);
      });
    });
  }
};

// Localized display of the fixed English structure (headings + action chips) —
// render-time only; the stored message text stays English.
const isEnglishUi = () => locale.value === 'en';

const displayLabel = (text: string, kind: 'heading' | 'chip'): string =>
  isEnglishUi() ? text : (localizeAskLabel(text, kind, t) ?? text);

// Runs last so the other enhancers still see the English labels they match on.
const enhanceStructureLabels = () => {
  const root = rootRef.value;
  if (!root || isEnglishUi()) return;
  const headings = root.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6, strong, b, dt, .ask-inline-kv-label');
  for (const el of Array.from(headings)) {
    if (el.children.length > 0) continue;
    const localized = localizeAskLabel(el.textContent ?? '', 'heading', t);
    if (localized) el.textContent = localized;
  }
  // Plain-text chip lines ("📝 Submit client as deal", often <br>-separated) that are not → buttons.
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const value = node.nodeValue ?? '';
    const localized = localizeAskLabel(value, 'chip', t);
    if (localized) node.nodeValue = value.replace(value.trim(), () => localized);
  }
};

const enhanceAll = () => {
  enhanceTables();
  enhanceImages();
  enhanceLinks();
  enhanceFollowUps();
  enhanceBadges();
  enhanceAvailabilityBadges();
  enhanceFactBlocks();
  enhanceConsecutiveFactLines();
  enhanceInlineKeyValues();
  enhanceStructureLabels();
};

const CARET_ID = 'ask-stream-caret';

const injectCaret = () => {
  const root = rootRef.value;
  if (!root) return;
  root.querySelector(`#${CARET_ID}`)?.remove();
  if (!isStreaming) return;
  // Append inline to the last visible text block so the caret sits
  // right after the last character rather than on its own line.
  const blocks = root.querySelectorAll<HTMLElement>('p, li, h1, h2, h3, h4, h5, h6, td, th');
  const target = blocks[blocks.length - 1] ?? root;
  const caret = document.createElement('span');
  caret.id = CARET_ID;
  caret.setAttribute('aria-hidden', 'true');
  target.appendChild(caret);
};

onMounted(() => {
  enhanceAll();
  injectCaret();
});

// Streaming re-renders blow away the DOM. Re-enhance on every html change.
watch(html, () => {
  nextTick(() => {
    enhanceAll();
    injectCaret();
  });
});

watch(
  () => isStreaming,
  (streaming) => {
    if (!streaming) {
      rootRef.value?.querySelector(`#${CARET_ID}`)?.remove();
    } else {
      nextTick(injectCaret);
    }
  },
);
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <div
    ref="rootRef"
    class="markdown-content text-[16px] leading-[1.6] text-[#1D1D1B]"
    v-html="html"
  />
  <!-- eslint-enable vue/no-v-html -->
</template>

<style scoped lang="scss">
.markdown-content {
  :deep(#ask-stream-caret) {
    display: inline-block;
    width: 2px;
    height: 0.82em;
    background-color: #d4a032;
    margin-inline-start: 2px;
    vertical-align: text-bottom;
    border-radius: 1px;
    animation: ask-caret-blink 1s ease-in-out infinite;
  }

  :deep(p) {
    margin: 0 0 1em;
  }
  :deep(p),
  :deep(ul),
  :deep(ol),
  :deep(blockquote),
  :deep(pre),
  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    max-width: 72ch;
  }
  :deep(> p:first-child) {
    font-size: 18px;
    line-height: 1.42;
    font-weight: 500;
    color: #1d1d1b;
    margin-bottom: 1.25em;
  }
  :deep(p:last-child) {
    margin-bottom: 0;
  }
  :deep(strong) {
    font-weight: 600;
    color: #1d1d1b;
  }
  :deep(em) {
    font-style: italic;
  }
  :deep(h1),
  :deep(h2),
  :deep(h3) {
    font-family: 'Fraunces', serif;
    font-weight: 600;
    letter-spacing: -0.015em;
    line-height: 1.2;
    margin: 1.5em 0 0.5em;
  }
  :deep(h1) {
    font-size: 24px;
  }
  :deep(h2) {
    font-size: 20px;
  }
  :deep(h3) {
    font-size: 17px;
  }
  :deep(ul),
  :deep(ol) {
    margin: 0.5em 0 0.85em;
    padding-inline-start: 1.4em;
  }
  :deep(li) {
    margin: 0.35em 0;
  }
  :deep(li::marker) {
    color: rgba(29, 29, 27, 0.42);
  }
  :deep(.ask-fact-block) {
    margin: 1.35em 0 1.65em;
    overflow: hidden;
    border: 1px solid #e5e6e1;
    border-radius: 12px;
    background: #ffffff;
  }
  :deep(.ask-fact-block dl) {
    margin: 0;
  }
  :deep(.ask-fact-row) {
    display: grid;
    grid-template-columns: minmax(170px, 0.36fr) minmax(0, 1fr);
    gap: 18px;
    padding: 11px 14px;
    border-bottom: 1px solid #e5e6e1;
  }
  :deep(.ask-fact-row:nth-child(even)) {
    background: #ffffff;
  }
  :deep(.ask-fact-row:last-child) {
    border-bottom: 0;
  }
  :deep(.ask-fact-row dt) {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0;
    color: #70736e;
  }
  :deep(.ask-fact-row dd) {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
    color: #1d1d1b;
    font-size: 14px;
    line-height: 1.45;
  }
  :deep(.ask-inline-kv-label) {
    font-weight: 700;
    color: #1d1d1b;
  }
  @media (max-width: 640px) {
    :deep(.ask-fact-row) {
      grid-template-columns: 1fr;
      gap: 3px;
    }
  }
  :deep(ul > li:has(> strong:first-child)),
  :deep(ol > li:has(> strong:first-child)),
  :deep(ul > li:has(> a:first-child)),
  :deep(ol > li:has(> a:first-child)) {
    margin: 0.55em 0;
    padding: 8px 10px;
    border: 1px solid #e5e6e1;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.72);
    box-shadow: 0 6px 18px -18px rgba(29, 29, 27, 0.35);
  }
  :deep(ul > li:has(> strong:first-child)::marker),
  :deep(ol > li:has(> strong:first-child)::marker),
  :deep(ul > li:has(> a:first-child)::marker),
  :deep(ol > li:has(> a:first-child)::marker) {
    color: #d4a032;
    font-weight: 700;
  }
  :deep(a) {
    color: #1d1d1b;
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-color: rgba(212, 160, 50, 0.6);
  }
  :deep(code) {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.88em;
    padding: 1px 5px;
    border-radius: 4px;
    background: rgba(29, 29, 27, 0.05);
  }
  :deep(pre) {
    background: rgba(29, 29, 27, 0.04);
    border: 1px solid #e5e6e1;
    border-radius: 8px;
    padding: 10px 12px;
    margin: 0.6em 0 1em;
    overflow-x: auto;
    // Long lines wrap: an answer's text is never cut off at the box's edge.
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    line-height: 1.5;
  }
  // Task lists ("- [ ] Confirm the escrow account"): a box in place of the bullet, aligned with its text.
  :deep(ul:has(> li > input[type='checkbox'], > li > p > input[type='checkbox'])) {
    padding-inline-start: 0.1em;
  }
  :deep(li:has(> input[type='checkbox'], > p > input[type='checkbox'])) {
    list-style: none;
    padding-inline-start: 1.75em;
    position: relative;
    margin: 0.5em 0;
  }
  :deep(li > input[type='checkbox']),
  :deep(li > p > input[type='checkbox']) {
    appearance: none;
    position: absolute;
    inset-inline-start: 0;
    top: 0.22em;
    width: 1.05em;
    height: 1.05em;
    margin: 0;
    border: 1.5px solid #c9c7bf;
    border-radius: 4px;
    background: #ffffff;
  }
  :deep(li > input[type='checkbox']:checked),
  :deep(li > p > input[type='checkbox']:checked) {
    border-color: #d4a032;
    background: #d4a032
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='white' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round' d='M4 8.5l2.6 2.6L12 5.6'/%3E%3C/svg%3E")
      center / 100% no-repeat;
  }
  :deep(pre code) {
    background: transparent;
    padding: 0;
    border-radius: 0;
    font-size: inherit;
  }
  :deep(blockquote) {
    border-inline-start: 2px solid rgba(212, 160, 50, 0.7);
    padding-block: 2px;
    padding-inline: 12px 0;
    margin: 1.2em 0;
    color: #70736e;
    font-style: italic;
  }
  :deep(table) {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin: 1.35em 0 1.65em;
    font-size: 14px;
    border: 1px solid #e5e6e1;
    border-radius: 8px;
    overflow: hidden;
  }
  :deep(thead) {
    background: #f7f7f4;
  }
  :deep(th) {
    text-align: start;
    padding: 12px 14px;
    font-size: 14px;
    line-height: 1.35;
    font-weight: 600;
    color: #70736e;
    border-bottom: 1px solid #e5e6e1;
    white-space: nowrap;
  }
  :deep(td) {
    min-height: 40px;
    padding: 11px 14px;
    border-bottom: 1px solid #e5e6e1;
    vertical-align: top;
  }
  :deep(tbody tr:nth-child(even)) {
    background: #fafaf7;
  }
  :deep(tbody tr:last-child td) {
    border-bottom: 0;
  }
  :deep(tbody tr:hover) {
    background: rgba(212, 160, 50, 0.05);
  }
  // Images inside table cells — thumbnail sized, inline with project-name link.
  :deep(td img) {
    display: inline-block;
    width: 44px;
    height: 32px;
    object-fit: cover;
    border-radius: 4px;
    margin-inline-end: 8px;
    vertical-align: middle;
    background: rgba(29, 29, 27, 0.05);
  }
  // Images inside list items are hidden — project cards below render photos
  // already, so an inline image in the bullet creates an ugly duplicate.
  :deep(li img) {
    display: none;
  }
  // Images in prose (advisory text, launch cards) — full-width renders.
  :deep(p img) {
    display: block;
    width: 100%;
    max-height: 220px;
    object-fit: cover;
    border-radius: 10px;
    margin: 0.5em 0;
    background: rgba(29, 29, 27, 0.04);
  }
  :deep(td a) {
    color: #1d1d1b;
    font-weight: 500;
    text-decoration: none;
    border-bottom: 1px solid rgba(212, 160, 50, 0.45);
    transition: border-color 0.15s;
  }
  // Clickable follow-up suggestion chips. Each "→ ..." line in the model's
  // answer becomes a chip that re-fires that exact prompt on click.
  :deep(.ask-followup-chip) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-block: 4px;
    margin-inline: 0 6px;
    padding: 7px 12px;
    border-radius: 999px;
    border: 1px solid rgba(212, 160, 50, 0.7);
    background: #ffffff;
    color: #8a6417;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    cursor: pointer;
    transition: all 0.15s;
  }
  :deep(.ask-followup-chip::before) {
    content: '→';
    color: #d4a032;
    font-weight: 700;
  }
  :deep(.ask-followup-chip:hover) {
    background: rgba(212, 160, 50, 0.1);
    border-color: #d4a032;
    transform: translateY(-1px);
  }
  :deep(td a:hover) {
    border-bottom-color: rgba(212, 160, 50, 1);
  }
  :deep(hr) {
    border: 0;
    border-top: 1px solid #e5e6e1;
    margin: 1.5em 0;
  }

  // Wrapper inserted around each rendered <table> at runtime so we can
  // anchor the "Export CSV" button directly under the table, right-aligned
  // within the table's own width. `overflow-x: auto` keeps wide tables
  // (>4 numeric cols) horizontally scrollable on mobile instead of forcing
  // the entire page to scroll sideways.
  :deep(.ask-table-wrap) {
    position: relative;
    margin: 1.35em 0 1.65em;
    display: block;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 8px;
    background:
      linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 34px) left center / 34px 100% no-repeat,
      linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 34px) right center / 34px 100% no-repeat,
      linear-gradient(90deg, rgba(29, 29, 27, 0.2), rgba(29, 29, 27, 0)) left center / 18px 100% no-repeat,
      linear-gradient(270deg, rgba(29, 29, 27, 0.22), rgba(29, 29, 27, 0)) right center / 18px 100% no-repeat;
    background-attachment: local, local, scroll, scroll;
  }
  :deep(.ask-table-scroll-hint) {
    position: sticky;
    inset-inline-start: 0;
    z-index: 4;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 0 0 8px;
    padding: 5px 10px;
    border: 1px solid rgba(212, 160, 50, 0.4);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.96);
    color: #8a6417;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    box-shadow: 0 8px 18px -16px rgba(29, 29, 27, 0.35);
  }
  :deep(.ask-table-wrap > table) {
    margin: 0;
    width: auto;
    min-width: max-content;
  }
  :deep(.ask-table-wrap > table th:first-child),
  :deep(.ask-table-wrap > table td:first-child) {
    position: sticky;
    inset-inline-start: 0;
    z-index: 1;
    max-width: 280px;
    background: inherit;
    box-shadow:
      1px 0 0 #e5e6e1,
      12px 0 20px -20px rgba(29, 29, 27, 0.32);
  }
  :deep(.ask-table-wrap > table th:first-child) {
    z-index: 2;
    background: #f7f7f4;
  }
  :deep(.ask-table-wrap > table tbody tr:nth-child(odd) td:first-child) {
    background: #ffffff;
  }
  :deep(.ask-table-wrap > table tbody tr:nth-child(even) td:first-child) {
    background: #fafaf7;
  }
  :deep(.ask-table-wrap > table tbody tr:hover td:first-child) {
    background: #fbf5e7;
  }
  :deep(.ask-csv-btn) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 8px;
    margin-inline-start: auto;
    min-height: 34px;
    padding: 6px 13px;
    background: #ffffff;
    border: 1px solid #e5e6e1;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #70736e;
    cursor: pointer;
    transition:
      background 0.15s,
      border-color 0.15s,
      color 0.15s;
  }
  :deep(.ask-csv-btn:hover) {
    border-color: rgba(212, 160, 50, 0.65);
    background: rgba(212, 160, 50, 0.08);
    color: #8a6417;
  }
  :deep(.ask-row-reply-cell) {
    width: 34px;
    padding: 4px 6px !important;
    text-align: center;
    vertical-align: middle;
  }
  :deep(.ask-row-reply-btn) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    color: #a3a5a0;
    cursor: pointer;
    opacity: 0;
    transition:
      opacity 0.15s,
      background 0.15s,
      border-color 0.15s,
      color 0.15s;
  }
  :deep(tr:hover .ask-row-reply-btn),
  :deep(.ask-row-reply-btn:focus-visible) {
    opacity: 1;
  }
  :deep(.ask-row-reply-btn:hover) {
    border-color: rgba(212, 160, 50, 0.65);
    background: rgba(212, 160, 50, 0.08);
    color: #8a6417;
  }
  :deep(.ask-table-row-hidden) {
    display: none;
  }
  :deep(.ask-table-more-btn) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 8px;
    margin-inline-start: auto;
    min-height: 36px;
    padding: 7px 14px;
    background: #ffffff;
    border: 1px solid rgba(212, 160, 50, 0.65);
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    color: #8a6417;
    cursor: pointer;
    transition:
      background 0.15s,
      border-color 0.15s,
      color 0.15s;
  }
  :deep(.ask-table-more-btn:hover) {
    background: rgba(212, 160, 50, 0.1);
    border-color: #d4a032;
  }
  // Price position badges — injected by enhanceBadges()
  :deep(.ask-price-badge) {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid;
    font-size: 12px;
    font-weight: 700;
  }
  :deep(.ask-price-badge[data-verdict='BELOW MARKET']) {
    background: #f0fdf4;
    color: #15803d;
    border-color: #86efac;
  }
  :deep(.ask-price-badge[data-verdict='FAIR']) {
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #93c5fd;
  }
  :deep(.ask-price-badge[data-verdict='PREMIUM']) {
    background: #fffbeb;
    color: #b45309;
    border-color: #fcd34d;
  }
  :deep(.ask-price-badge[data-verdict='OVERPRICED']) {
    background: #fef2f2;
    color: #b91c1c;
    border-color: #fca5a5;
  }
  :deep(.ask-price-badge[data-verdict='INSUFFICIENT DATA']) {
    background: #f9fafb;
    color: #6b7280;
    border-color: #d1d5db;
  }
  // Overall verdict badge — injected by enhanceBadges()
  :deep(.ask-verdict-badge) {
    display: inline-flex;
    align-items: center;
    padding: 4px 14px;
    border-radius: 999px;
    border: 1.5px solid;
    font-size: 12px;
    font-weight: 700;
    margin-top: 6px;
  }
  // Same tones as the older labels they replace: green below market, blue at it, amber and red above.
  :deep(.ask-verdict-badge[data-verdict='STRONG OPPORTUNITY']) {
    background: #f0fdf4;
    color: #15803d;
    border-color: #4ade80;
  }
  :deep(.ask-verdict-badge[data-verdict='ATTRACTIVE PRICE']) {
    background: #f0fdf4;
    color: #166534;
    border-color: #86efac;
  }
  :deep(.ask-verdict-badge[data-verdict='PREMIUM PRICED']) {
    background: #fffbeb;
    color: #b45309;
    border-color: #fcd34d;
  }
  :deep(.ask-verdict-badge[data-verdict='NEEDS CAUTION']) {
    background: #fef2f2;
    color: #b91c1c;
    border-color: #fca5a5;
  }
  :deep(.ask-verdict-badge[data-verdict='STRONG BUY']) {
    background: #f0fdf4;
    color: #15803d;
    border-color: #4ade80;
  }
  :deep(.ask-verdict-badge[data-verdict='GOOD BUY']) {
    background: #f0fdf4;
    color: #166534;
    border-color: #86efac;
  }
  :deep(.ask-verdict-badge[data-verdict='FAIR DEAL']) {
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #93c5fd;
  }
  :deep(.ask-verdict-badge[data-verdict='NEGOTIATE']) {
    background: #fffbeb;
    color: #b45309;
    border-color: #fcd34d;
  }
  :deep(.ask-verdict-badge[data-verdict='OVERPRICED']) {
    background: #fef2f2;
    color: #b91c1c;
    border-color: #fca5a5;
  }
  :deep(.ask-verdict-badge[data-verdict='AVOID']) {
    background: #fef2f2;
    color: #7f1d1d;
    border-color: #f87171;
  }
  :deep(.ask-availability-confidence) {
    display: flex;
    max-width: 72ch;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 1.3em 0 1.5em;
    padding: 10px 12px;
    border: 1px solid #e5e6e1;
    border-radius: 8px;
    background: #ffffff;
  }
  :deep(.ask-availability-badge) {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 4px 11px;
    border: 1px solid;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 700;
    line-height: 1.2;
  }
  :deep(.ask-availability-badge[data-tone='good']) {
    border-color: rgba(44, 122, 75, 0.24);
    background: #edf7f0;
    color: #2c7a4b;
  }
  :deep(.ask-availability-badge[data-tone='warn']) {
    border-color: rgba(180, 83, 9, 0.24);
    background: #fff7e6;
    color: #9a5f12;
  }
  :deep(.ask-availability-badge[data-tone='bad']) {
    border-color: rgba(185, 28, 28, 0.22);
    background: #fef2f2;
    color: #9f1d1d;
  }
  :deep(.ask-availability-badge[data-tone='neutral']) {
    border-color: rgba(112, 115, 110, 0.24);
    background: #f7f7f4;
    color: #70736e;
  }
  :deep(.ask-availability-copy) {
    min-width: 0;
    color: #70736e;
    font-size: 14.5px;
    line-height: 1.45;
  }
  // "Project image pending" placeholder — shown when AI emits ![name]() with no URL
  :deep(.ask-img-pending) {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    margin: 0.4em 0;
    background: rgba(13, 15, 21, 0.03);
    border: 1px dashed rgba(13, 15, 21, 0.15);
    border-radius: 8px;
    font-size: 12px;
    color: rgba(13, 15, 21, 0.4);
    font-style: italic;
    svg {
      flex-shrink: 0;
      color: rgba(13, 15, 21, 0.25);
    }
  }
}

@keyframes ask-caret-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.15;
  }
}
</style>
